import singleSpa from "single-spa";
import { io } from "socket.io";
import store from "../store";
import { i18n } from "./i18n";
const dayjs = require("dayjs").default;
import { toThousandFilter, uppercaseFirst, quotaSize } from "./filters";
import trialServices from "../services/trial";
import tempBalanceServices from "../services/temp-balance";
import CONSTANTS from "../config/constants";
import RULES from "../config/rules";
import URLS from "../config/urls";
import { Notification } from "bizfly-ui";

const {
  TRIAL_SERVICES,
  DEFAULT_DISK_MIN,
  TEMP_BALANCE_SERVICES,
  PRIVATE_REGIONS,
  WHITELIST_SERVICES,
  NO_PAYMENT_SERVICES,
  ACTIVE_TRIAL_MANUAL,
} = CONSTANTS;
const { DIGITS_RE, SUBNET, IP_ADDRESS } = RULES;

export function getErrorMessage(err) {
  return (
    checkType(err?.response?.data?.errors?.[0]?.detail) ||
    checkType(err?.response?.data?._error?.message) ||
    checkType(err?.response?.data?.meta?.detail) ||
    checkType(err?.response?.data?.meta?.message) ||
    checkType(err?.response?.data?.meta?.description) ||
    checkType(err?.response?.data?.meta?.error) ||
    checkType(err?.response?.data?._schema?.[0]) ||
    checkType(err?.response?.data?.message?.message) ||
    checkType(err?.response?.data?.message?.action) ||
    checkType(err?.response?.data?.detail?.message) ||
    checkType(err?.response?.data?.message) ||
    checkType(err?.response?.data?.detail) ||
    checkType(err?.response?.data?.detail?.error) ||
    checkType(err?.response?.data?.detail?.description) ||
    checkType(err?.response?.data?.error) ||
    checkType(err?.response?.data?.reason) ||
    checkType(err?.message)
  );
}

export function checkPayment({ currentService, isWhitelist }) {
  const { currentRegion } = store.endpoint.state;
  const isPrivateRegion = PRIVATE_REGIONS.includes(currentRegion);
  const isNoPayment = NO_PAYMENT_SERVICES.includes(currentService?.slug);
  return !isPrivateRegion && !isWhitelist && !isNoPayment;
}

export function checkWhitelist({ currentService, defaultSlug, userProfile }) {
  const whiteListService =
    WHITELIST_SERVICES[currentService?.slug] ?? defaultSlug;
  return userProfile?.whitelist?.includes(whiteListService);
}

export async function fetchInfoTrials() {
  try {
    const { currentService } = store.services.state;
    const { userProfile, ownerProfile, myProfile } = store.user.state;
    const defaultSlug = currentService?.slug?.split("/")?.[1];

    const isIamService = currentService?.supportIAM ?? false;
    let response = {};
    const type = TRIAL_SERVICES?.[store?.services?.state?.currentService?.slug];

    const params = {
      ...(isIamService && { using_iam: 1 }),
      service_type: type,
    };
    response = await trialServices.trial(params);

    const { trials } = response;
    const trialSelected =
      trials?.find((service) => service?.service_name === type) ?? {};
    if (isIamService) {
      store.user.state.ownerProfile = {
        ...ownerProfile,
        trials: trialSelected,
      };
    } else {
      store.user.state.myProfile = {
        ...myProfile,
        trials: trialSelected,
      };
    }
    const newUserProfile = {
      ...(isIamService
        ? { ...store.user.state.ownerProfile }
        : { ...store.user.state.myProfile }),
    };

    store.trial.state.trials = trials;
    if (isIamService) store.trial.state.trialsIam = trials;

    const isWhitelist = checkWhitelist({
      currentService,
      defaultSlug,
      userProfile: newUserProfile,
    });
    const isCheckPayment = checkPayment({ currentService, isWhitelist });

    store.user.state.userProfile = {
      ...newUserProfile,
      trials: trialSelected,
      ...hasPermission({ userProfile: newUserProfile, isCheckPayment }),
      isWhitelist,
    };
  } catch (error) {
    console.warn(error);
    return {};
  }
}

export async function fetchInfoTempBalance() {
  try {
    const { userProfile } = store?.user?.state;
    const { currentService } = store?.services?.state;
    const defaultSlug = currentService?.slug?.split("/")?.[1];
    const type = TEMP_BALANCE_SERVICES?.[currentService?.slug] ?? defaultSlug;
    const params = {
      service_type: type,
    };
    const response = await tempBalanceServices.getTempBalances(params);
    const tempBalanceSelected = response[type];
    store.tempBalance.state.tempBalances = response;
    store.user.state.userProfile = {
      ...userProfile,
      tempBalance: tempBalanceSelected,
    };
  } catch (error) {
    console.error(error);
    return {};
  }
}

export function googleTracking(data) {
  const email = store.user.state.userProfile.email;
  const ga = {
    cid: email,
    cm: "web",
    cn: "",
    cs: "dashboard",
    ea: "Click",
    ec: "",
    el: "",
    ev: 1,
    t: "event",
    tid: "UA-62110417-4",
    uid: email,
    v: 1,
    ...data,
  };
  const queryString = new URLSearchParams(ga).toString();
  const url = `https://www.google-analytics.com/collect?${queryString}`;
  const image = document.createElement("img");
  image.style.width = "0px";
  image.style.height = "0px";
  image.style.display = "none";
  image.src = url;
  image.id = "image-analytics-" + new Date().getTime();
  document.getElementsByTagName("body")[0].appendChild(image);
  image.remove();
}

export function setTitle(title) {
  if (!title.includes("404")) {
    document.title = title;
    if (document.setTitle) {
      clearTimeout(document.setTitle);
    }
    return;
  }
  document.setTitle = setTimeout(() => {
    document.title = title;
  }, 1000);
}

export function debounce(func, wait) {
  // Cách dùng
  // searchFunc: debounce(function () {
  //   ...
  // }, 500),
  let timeout;

  return function () {
    const context = this;
    const args = arguments;

    const executeFunction = function () {
      func.apply(context, args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(executeFunction, wait);
  };
}

export function copyText(self, text) {
  self.$clipboard(text);
  self.$notify({
    title: self.$t("notification.success.title"),
    message: self.$t("notification.copied"),
    type: "success",
    customClass: "success",
  });
}

export function parseDataPagination(data, page = 1, limit = 10) {
  if (!data?.length) return [];
  const offset = (page - 1) * limit;
  return data.slice(offset).slice(0, limit);
}

/**
 * Get current datetime
 */
export function now(timestamp = true) {
  if (timestamp) {
    return dayjs().unix();
  }
  return dayjs();
}

/**
 * Get current datetime
 */
export function toTimestamp(datetime) {
  return dayjs(datetime).unix();
}

export function today() {
  return dayjs().format("DD/MM/YYYY");
}

export function nextThreeDays() {
  const millisecondsOf3Days = 259200000;
  return dayjs(Date.now() + millisecondsOf3Days).format("DD/MM/YYYY");
}

export function thisMonth() {
  return dayjs().month() + 1;
}

export function lastDayOfMonth() {
  return dayjs().endOf("month").format("DD/MM/YYYY");
}

export function checkType(message, type = "string") {
  if (typeOf(message) === type) return message;
  return false;
}

export function checkIamVerify(userProfile) {
  let verifiedEmail = userProfile?.verified_email;
  let verifiedPhone = userProfile?.verified_phone;
  let verifiedPayment = userProfile?.verified_payment;
  return { verifiedEmail, verifiedPayment, verifiedPhone };
}

/**
 * Check permission
 */
export function hasPermission({ userProfile, isCheckPayment = true }) {
  const { verifiedEmail, verifiedPayment, verifiedPhone } =
    checkIamVerify(userProfile);
  const flag = {
    access: true,
    msg: i18n.t("permission.notHaveAccess"),
  };
  const isReader = userProfile?.role?.toLowerCase() === "reader";
  const errMsg = [];
  if (isReader) {
    flag.access = false;
    errMsg.push(i18n.t("permission.actionDenied"));
  }
  if (!verifiedEmail || !verifiedPhone) {
    flag.access = false;
    errMsg.push(i18n.t("permission.unverified"));
  }
  if (isCheckPayment) {
    if (!verifiedPayment) {
      flag.access = false;
      errMsg.push(i18n.t("permission.unverifiedPayment"));
    }
    const currentSlug = `/${window.location.pathname.split("/")?.[1]}`;
    if (
      ACTIVE_TRIAL_MANUAL.includes(currentSlug) &&
      userProfile?.trials?.level === 0
    ) {
      flag.access = false;
      errMsg.push(i18n.t("permission.notActiveTrial"));
    }
    if (userProfile?.trials?.active && !userProfile?.trials?.enable) {
      flag.access = false;
      errMsg.push(i18n.t("permission.trialExpires"));
    }
    if (userProfile?.trials?.level > 1 && userProfile?.has_expired_invoice) {
      flag.access = false;
      errMsg.push(i18n.t("permission.unpaidInvoice"));
    }
    if (
      userProfile?.trials?.level > 1 &&
      userProfile?.has_expired_payment_request
    ) {
      flag.access = false;
      errMsg.push(i18n.t("permission.unpaidDeposit"));
    }
    if (userProfile?.billing_balance < 0 && userProfile?.negative_balance) {
      flag.access = false;
      errMsg.push(i18n.t("permission.negativeAccount"));
    }
  }
  flag.msg += errMsg.join(", ");
  return flag;
}

export function generateRandomCharacters(length = 3) {
  return Math.random()
    .toString(36)
    .replace(/[^a-z0-9]+/g, "")
    .substr(0, length);
}

export function randomPassword(length = 18) {
  const content =
    "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!@#$%^&*()-_+=.,<>?`~/\\|[]{}";
  return Array.from(crypto.getRandomValues(new Uint32Array(length)))
    .map((x) => content[x % content.length])
    .join("");
}

export function formatDiskSize(
  value,
  min = DEFAULT_DISK_MIN,
  max = Number.MAX_SAFE_INTEGER
) {
  let result = value;
  if (!value || value < min) result = min;
  else if (value > max) result = max;
  else if (value % 10 < 5) result = value - (value % 10);
  else result = value + 10 - (value % 10);
  return result;
}

export function freeDataTransfer({ costFlavor }) {
  const maxSize = 12;
  const result = Math.ceil(costFlavor / 1000 ** 2);
  if (result > maxSize) return maxSize;
  return result;
}

export function freeBandwidth(cpu, category) {
  if (category === "premium") {
    if (cpu <= 12) return 1;
    return 2;
  } else if (["enterprise", "dedicated"].includes(category)) {
    if (cpu <= 12) return 1;
    if (cpu <= 24) return 2;
    if (cpu <= 36) return 3;
    return 4;
  }
  return 1;
}

export function getCookie(cname) {
  let matches = document.cookie.match(
    new RegExp(
      "(?:^|; )" +
        cname.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, "\\$1") +
        "=([^;]*)"
    )
  );
  return matches ? decodeURIComponent(matches[1]) : undefined;
}

export function getPercent({ number1, number2, round = 2 }) {
  let result = 0;
  if (number2) {
    result = (number1 / number2) * 100;
  }
  return Number(result.toFixed(round));
}

export function formatCpu(data, category) {
  const cpuConfig = {};
  data?.forEach((flavor) => {
    const isCheckCategory = category ? flavor?.category === category : true;
    if (isCheckCategory) {
      if (!Object.keys(cpuConfig).includes(String(flavor?.vcpus))) {
        cpuConfig[flavor?.vcpus] = [];
      }
      cpuConfig[flavor?.vcpus].push(flavor?.ram / 1024);
    }
  });
  return cpuConfig;
}

export function disableScroll() {
  document.body.style.height = "100%";
  document.body.style.overflow = "hidden";
}

export function enableScroll() {
  document.body.style.height = null;
  document.body.style.overflow = null;
}

export function numberWithDelimiter(number, delimiter = ".") {
  return Math.round(number)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, delimiter);
}

export function scrollToElement(id, height = 138) {
  const element = document.getElementById(id);
  const y = element.getBoundingClientRect().top - height;
  window.scrollBy({ top: y, behavior: "smooth" });
}

export function versionCompare(v1, v2, options) {
  const lexicographical = options && options.lexicographical;
  const zeroExtend = options && options.zeroExtend;
  let v1parts = v1.startsWith("v") ? v1.slice(1).split(".") : v1.split(".");
  let v2parts = v2.startsWith("v") ? v2.slice(1).split(".") : v2.split(".");

  function isValidPart(x) {
    return (lexicographical ? /^\d+[A-Za-z]*$/ : /^\d+$/).test(x);
  }

  if (!v1parts.every(isValidPart) || !v2parts.every(isValidPart)) {
    return NaN;
  }

  if (zeroExtend) {
    while (v1parts.length < v2parts.length) v1parts.push("0");
    while (v2parts.length < v1parts.length) v2parts.push("0");
  }

  if (!lexicographical) {
    v1parts = v1parts.map(Number);
    v2parts = v2parts.map(Number);
  }

  for (let i = 0; i < v1parts.length; i += 1) {
    if (v2parts.length === i) {
      return 1;
    }

    if (v1parts[i] === v2parts[i]) {
      // eslint-disable-next-line
      continue;
    } else if (v1parts[i] > v2parts[i]) {
      return 1;
    } else {
      return -1;
    }
  }

  if (v1parts.length !== v2parts.length) {
    return -1;
  }

  return 0;
}

export function getRandomColor(colorRange) {
  // colorRange là khoảng đậm nhạt của màu sắc, từ 0 đến 256, càng nhỏ màu càng đậm (mà tối dark);
  let lvl = 0;
  if (colorRange) {
    lvl = colorRange;
  }
  const color =
    (Math.floor(Math.random() * lvl) << 16) |
    (Math.floor(Math.random() * lvl) << 8) |
    Math.floor(Math.random() * lvl);
  return `#${color.toString(16).padStart(6, "0")}`;
}

export function getRandomInt(min, max) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function sortObjectKeys(obj) {
  if (!obj || typeof obj !== "object") return {};

  const entriesSort = Object.entries(obj).sort((entry1, entry2) => {
    return entry1[0].localeCompare(entry2[0], "en", {
      sensitivity: "case",
      numeric: true,
    });
  });
  return Object.fromEntries(entriesSort);
}

export function addUrlParams(url, key, value) {
  try {
    const nextUrl = new URL(url);
    nextUrl.searchParams.set(key, value);
    return nextUrl.href;
  } catch (error) {
    return url;
  }
}

export function showDocFrame(inputUrl, text = "") {
  const arrayDomainList = [URLS.docsUrl, URLS.docsBetaUrl];
  const url = inputUrl.replace(
    "docs-beta.bizflycloud.vn",
    "docs.bizflycloud.vn"
  );
  if (arrayDomainList.some((domain) => url.includes(domain))) {
    const payload = {
      visible: true,
      title: text,
      href: addUrlParams(url, "embedded", "true"), // add "?embedded=true" to url
    };
    store.layout.state.docFramePopup = payload;
  } else {
    window.open(url, "_blank");
  }
}

export function observerElement(
  element,
  onVisible = () => {},
  onHidden = () => {},
  options = {
    threshold: 1,
  }
) {
  if (!element || !IntersectionObserver) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry?.isIntersecting) {
        onVisible?.();
        return;
      }
      onHidden?.();
    });
  }, options);

  observer.observe(element);
}

export function checkIsNewVersion(slug) {
  if (!slug) {
    return false;
  }
  const services = store?.services?.state?.allServices
    ?.map((service) => service?.list)
    ?.flat(1);
  const checkService = services.findIndex((service) => service.slug === slug);
  if (checkService === -1) {
    return false;
  }
  if (services?.[checkService]?.newVersion) {
    return true;
  }
  return false;
}
export function getLocalStorageData(tableName) {
  if (localStorage.getItem(`setting-table-${this.currentService.name}`)) {
    const result = JSON.parse(
      localStorage.getItem(`setting-table-${this.currentService.name}`)
    );
    const currentServiceSetting = result.find(
      (setting) => setting.slug === this.currentService.slug
    );
    return currentServiceSetting.table[tableName];
  }
  return null;
}
export function longToIp(ipInt) {
  return (
    (ipInt >>> 24) +
    "." +
    ((ipInt >> 16) & 255) +
    "." +
    ((ipInt >> 8) & 255) +
    "." +
    (ipInt & 255)
  );
}
export function ipToLong(ipAddress) {
  const ip = ipAddress.match(IP_ADDRESS);
  if (ip) {
    let iplong = 0;
    let power = 1;
    for (let i = 4; i >= 1; i -= 1) {
      iplong += power * Number(ip[i]);
      power *= 256;
    }
    return iplong;
  }
  return -1;
}
//check ip in subnet
export function inSubNet(ip, subnet) {
  if (ip === "") return true;
  const mask = subnet.match(SUBNET);
  const baseIp = ipToLong(mask[1]);
  const longIp = ipToLong(ip);

  if (mask && baseIp >= 0) {
    const freedom = 2 ** (32 - Number(mask[2]));
    return (
      (longIp > baseIp || longIp === baseIp) &&
      (longIp < baseIp + freedom - 1 || longIp === baseIp + freedom - 1)
    );
  }
  return false;
}
//range ip
export function rangeIp(subnet) {
  const mask = subnet.match(SUBNET);
  const baseIp = ipToLong(mask[1]);
  const freedom = 2 ** (32 - Number(mask[2]));
  return `${longToIp(baseIp)} - ${longToIp(baseIp + freedom - 1)}`;
}

export function typeOf(value) {
  return Object.prototype.toString.call(value).slice(8, -1).toLowerCase();
}

export function spaNavigateGuard(to) {
  if (
    window?.confirm &&
    store?.loading?.state?.processingTask?.isProcessing &&
    !window.confirm(store?.loading?.state?.processingTask?.msg)
  )
    return;

  singleSpa.navigateToUrl(to);
}

export function checkSameObject(oldObj, currentObj) {
  const oldObjKeys = Object.keys(oldObj);

  if (oldObjKeys?.length !== Object.keys(currentObj)?.length) return false;

  return oldObjKeys.every(
    (key) => JSON.stringify(currentObj[key]) === JSON.stringify(oldObj[key])
  );
}

export function checkNonEmptyObject(object, listKey) {
  return Object.keys(object)
    .filter((extractKey) => !listKey.includes(extractKey))
    .every((key) => object[key]);
}

export function formatPlanName(name) {
  const planName = uppercaseFirst(name?.split(":")?.[1].replaceAll("_", " "));
  if (planName.includes("Saving plan")) {
    return planName.replace("Saving plan", "Subscription");
  }
  return planName;
}
export function formatString(str) {
  return str.replace(/[._]/g, " ");
}
export function formatNewPlanName(name) {
  if (!name) return "";
  const parts = name.split(":");
  const billingPlan = uppercaseFirst(parts[2]?.replaceAll("_", " ")) || "";
  if (parts.length === 2) {
    return uppercaseFirst(formatString(parts[1]));
  }
  if (parts.length === 3) {
    const config = formatString(parts[1].split(".")[1].toLowerCase());
    const additionalPart = parts[1].split(".")[2] || "";
    return `${billingPlan} ${config} ${additionalPart}`.trim();
  }
  return "";
}

export function currying(fn, ...args) {
  return (...arg) => fn(...args, ...arg);
}

export function downloadFile(filename, data) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(data);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.parentElement.removeChild(a);
}

// This function converts the string to lowercase, then perform the conversion
export function toLowerCaseNonAccentVietnamese(str) {
  let convertStr = str;
  convertStr = convertStr.toLowerCase();
  convertStr = convertStr.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, "a");
  convertStr = convertStr.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, "e");
  convertStr = convertStr.replace(/ì|í|ị|ỉ|ĩ/g, "i");
  convertStr = convertStr.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, "o");
  convertStr = convertStr.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, "u");
  convertStr = convertStr.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, "y");
  convertStr = convertStr.replace(/đ/g, "d");
  // Some system encode vietnamese combining accent as individual utf-8 characters
  convertStr = convertStr.replace(/\u0300|\u0301|\u0303|\u0309|\u0323/g, ""); // Huyền sắc hỏi ngã nặng
  convertStr = convertStr.replace(/\u02C6|\u0306|\u031B/g, ""); // Â, Ê, Ă, Ơ, Ư
  return convertStr;
}

// This function keeps the casing unchanged for str, then perform the conversion
export function toNonAccentVietnamese(str) {
  let convertStr = str;
  convertStr = convertStr.replace(/A|Á|À|Ã|Ạ|Â|Ấ|Ầ|Ẫ|Ậ|Ă|Ắ|Ằ|Ẵ|Ặ/g, "A");
  convertStr = convertStr.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, "a");
  convertStr = convertStr.replace(/E|É|È|Ẽ|Ẹ|Ê|Ế|Ề|Ễ|Ệ/, "E");
  convertStr = convertStr.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, "e");
  convertStr = convertStr.replace(/I|Í|Ì|Ĩ|Ị/g, "I");
  convertStr = convertStr.replace(/ì|í|ị|ỉ|ĩ/g, "i");
  convertStr = convertStr.replace(/O|Ó|Ò|Õ|Ọ|Ô|Ố|Ồ|Ỗ|Ộ|Ơ|Ớ|Ờ|Ỡ|Ợ/g, "O");
  convertStr = convertStr.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, "o");
  convertStr = convertStr.replace(/U|Ú|Ù|Ũ|Ụ|Ư|Ứ|Ừ|Ữ|Ự/g, "U");
  convertStr = convertStr.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, "u");
  convertStr = convertStr.replace(/Y|Ý|Ỳ|Ỹ|Ỵ/g, "Y");
  convertStr = convertStr.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, "y");
  convertStr = convertStr.replace(/Đ/g, "D");
  convertStr = convertStr.replace(/đ/g, "d");
  // Some system encode vietnamese combining accent as individual utf-8 characters
  convertStr = convertStr.replace(/\u0300|\u0301|\u0303|\u0309|\u0323/g, ""); // Huyền sắc hỏi ngã nặng
  convertStr = convertStr.replace(/\u02C6|\u0306|\u031B/g, ""); // Â, Ê, Ă, Ơ, Ư
  return convertStr;
}

export function connectSocket(endpoint, options = {}, store) {
  const socket = io(endpoint, options);
  socket.on("connect", () => {
    store.dispatch("socket/updateSocket", socket);
  });
  socket.on("disconnect", () => {
    store.dispatch("socket/updateSocket", null);
  });
}

export function disconnectSocket() {
  const socket = store?.socket?.state?.socket;
  if (socket) socket.disconnect();
}

export function showNotification(title, payload) {
  if (Notification.permission === "granted") {
    const notification = new Notification(title, {
      ...payload,
      icon: "https://statistic-resource.hn.ss.bfcplatform.vn/brand.png",
    });
    notification.addEventListener("error", (e) => {
      console.log("error noti");
    });
  }
}

export function hideTooltipWhenScroll() {
  // add func to hook mounted in list.vue
  const table = document.querySelector(".el-table__body-wrapper");
  table.addEventListener("scroll", (event) => {
    const tooltips = document.querySelectorAll(".el-tooltip");
    tooltips.forEach((tooltip) => {
      tooltip.blur();
    });
  });
}

export function formatQuota(quotas) {
  const listQuotas = [
    [
      {
        ...quotas?.compute?.cores,
        key: "cpu",
      },
      {
        ...quotas?.compute?.ram,
        key: "ram",
        isRam: true,
        useCapacity: true,
      },
      {
        ...quotas?.compute?.instances,
        key: "server",
      },
      {
        ...quotas?.compute?.key_pairs,
        key: "ssh_key",
      },
    ],
    [
      ...quotas?.storage?.gigabytes.map((gigabyte) => ({
        ...gigabyte,
        display_name: `${i18n.t(
          "quotas.volumeStorage"
        )} ${gigabyte?.category?.toUpperCase()} ${gigabyte?.display_name}`,
        key: `${gigabyte?.category?.toLowerCase()}_${gigabyte?.display_name?.toLowerCase()}`,
        useCapacity: true,
      })),
      {
        ...quotas?.storage?.volumes,
        key: "volume",
      },
      {
        ...quotas?.storage?.snapshots,
        key: "snapshot",
      },
    ],
    [
      {
        ...quotas?.network?.[0],
        key: "firewall",
      },
      {
        ...quotas?.network?.[1],
        key: "firewall_rule",
      },
      {
        ...quotas?.network?.[2],
        key: "vpc_network",
      },
    ],
  ];
  return listQuotas.map((quota, index) => {
    let isWarning = false;
    const items = quota?.map((item) => {
      const percentage = getPercent({
        number1: item?.in_use,
        number2: item?.limit,
      });
      if (percentage >= 70) {
        isWarning = true;
      }
      return {
        ...item,
        used: item?.useCapacity
          ? quotaSize(item?.in_use, item?.isRam)
          : item?.in_use,
        max: item?.useCapacity
          ? quotaSize(item?.limit, item?.isRam)
          : item?.limit,
        title: item?.display_name,
        percentage,
      };
    });
    const progressItems = {
      isWarning,
      title: Object.keys(quotas)?.[index],
      items,
    };
    return progressItems;
  }, []);
}

let computes = [];
let storages = [];
let networks = [];

export function showNotifyQuota(keys = []) {
  const generateId = generateRandomCharacters();
  let nameQuota = "";
  const checkKey = (array) =>
    keys.some((key) => array.includes(key?.toLowerCase()));
  if (checkKey(["volume", "snapshot"])) {
    computes = keys;
    nameQuota = "Storage";
  } else if (checkKey(["server", "ram", "cpu"])) {
    storages = keys;
    nameQuota = "Compute";
  } else if (checkKey(["firewall"])) {
    networks = keys;
    nameQuota = "Network";
  }
  Notification.warning({
    title: i18n.t("notification.title"),
    dangerouslyUseHTMLString: true,
    type: "warning",
    customClass: "warning",
    duration: 0,
    message: `
      <p>${i18n.t("quotas.invalidQuotas.titleWithName", {
        name: nameQuota,
      })}</p>
      <button id="btn-quota-info-${generateId}" class="mt-xs el-button bf-button el-button--warning el-button--default"> <span> <div class="button-content">${i18n.t(
      "quotas.invalidQuotas.detail",
      { name: nameQuota }
    )}</div></span></button>
    `,
  });
  const buttonShowQuota = document.getElementById(
    `btn-quota-info-${generateId}`
  );
  buttonShowQuota.addEventListener("click", () => {
    let resultQuota = keys;
    if (checkKey(["volume", "snapshot"])) {
      resultQuota = computes;
    } else if (checkKey(["server", "ram", "cpu"])) {
      resultQuota = storages;
    } else if (checkKey(["firewall"])) {
      resultQuota = networks;
    }
    store.quota.state.quotaKeys = resultQuota;
    store.quota.state.visibleQuota = true;
  });
}

export function formatFlavors(flavors) {
  return flavors.reduce(
    (acc, obj) => {
      if (!acc.ram.includes(obj?.ram / 1024)) {
        acc.ram.push(obj?.ram / 1024);
      }
      if (!acc.vcpus.includes(obj?.vcpus)) {
        acc.vcpus.push(obj?.vcpus);
      }
      return acc;
    },
    { ram: [], vcpus: [] }
  );
}

export function formatFlavorsInstance(flavors) {
  return flavors.map((flavor) => `cs:${flavor?.name}`);
}
